/**
 * useClipboard Hook
 *
 * Provides clipboard copy functionality with visual feedback.
 * Handles browser Clipboard API with graceful error handling.
 *
 * @param {number} resetTimeout - Duration in ms before resetting isCopied state (default: 2000ms)
 * @returns {{ copyToClipboard: Function, isCopied: boolean }}
 */

import { useState, useCallback, useEffect, useRef } from 'react';

export function useClipboard(resetTimeout = 2000) {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copyToClipboard = useCallback(
    async (text) => {
      // Check if Clipboard API is available
      if (!navigator.clipboard) {
        console.error('Clipboard API not available');
        return;
      }

      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Reset isCopied after timeout
        timeoutRef.current = setTimeout(() => {
          setIsCopied(false);
        }, resetTimeout);
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        setIsCopied(false);
      }
    },
    [resetTimeout]
  );

  return { copyToClipboard, isCopied };
}
